// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";

import queryString from "query-string";

import { AvailableFilters } from "components/elements/SearchBarWithFilters/_utils/types";
import { changeActiveOrganization as changeActiveOrganizationAction } from "containers/Auth/actions";
import { getActiveOrganizationSelector } from "containers/Auth/selectors";
import { ApiResponse } from "types/api";
import { removeQueryParamsFromLocation } from "utils/url";

export function useFilters(
  data: ApiResponse<Record<string, any>[]> | undefined
): AvailableFilters {
  return useMemo(
    () => ({
      sections: [
        {
          title: "Filters",
          options: (data?.results || []).map((filter) => ({
            id: filter.id,
            description: filter.attributes?.description,
            displayName: filter.attributes?.display_name,
            hasSuggestions: filter.attributes?.has_suggestions,
            param: filter.attributes?.param,
            // @todo TAP-80 Handle values.
            value: [],
          })),
        },
      ],
    }),
    [data]
  );
}

export const useToggle = (initialState = false): [boolean, () => void] => {
  const [state, setState] = useState<boolean>(initialState);
  const toggle = useCallback((): void => setState((state) => !state), []);
  return [state, toggle];
};

export const useSearchParams = () => {
  const location = useLocation();
  return useMemo(() => queryString.parse(location.search), [location.search]);
};

export const useRouter = () => ({
  location: useLocation(),
  navigate: useNavigate(),
  params: useParams(),
  match: useMatch(location.pathname),
});

interface UseDragAndDrop {
  getRowDragProps: (
    index: number,
    move: (from: number, to: number) => void
  ) => {
    onDragOver?: (e: React.DragEvent) => void;
    onDragLeave?: () => void;
    onDrop?: (e: React.DragEvent) => void;
  };
  getDragHandleProps: (index: number) => {
    onDragStart: () => void;
    onDragEnd: () => void;
  };
  state: {
    isDragging: (index: number) => boolean;
    isDragOver: (index: number) => boolean;
  };
}

export function useDragAndDrop(): UseDragAndDrop {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (
    e: React.DragEvent,
    dropIndex: number,
    move: (from: number, to: number) => void
  ) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      move(draggedIndex, dropIndex);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const isDragging = (index: number) => draggedIndex === index;
  const isDragOver = (index: number) => dragOverIndex === index;

  const getRowDragProps = (
    index: number,
    move: (from: number, to: number) => void
  ) => ({
    onDragOver: (e: React.DragEvent) => handleDragOver(e, index),
    onDragLeave: handleDragLeave,
    onDrop: (e: React.DragEvent) => handleDrop(e, index, move),
  });

  const getDragHandleProps = (index: number) => ({
    onDragStart: () => handleDragStart(index),
    onDragEnd: handleDragEnd,
  });

  return {
    getRowDragProps,
    getDragHandleProps,
    state: {
      isDragging,
      isDragOver,
    },
  };
}

interface UseActiveOrganization {
  activeOrganization?: {
    id: string;
    img?: string;
    name: string;
    roles: string[];
  };
  changeActiveOrganization: (organizationId: string) => Promise<void>;
  isChangingActiveOrganization: boolean;
  changingActiveOrganizationError?: string;
}

export const useActiveOrganization = (): UseActiveOrganization => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const activeOrganization = useSelector(getActiveOrganizationSelector);
  const [
    isChangingActiveOrganizationCount,
    setIsChangingActiveOrganizationCount,
  ] = useState<number>(0);
  const [changingActiveOrganizationError, setChangingActiveOrganizationError] =
    useState<string | undefined>(undefined);

  const changeActiveOrganization = useCallback(
    async (organizationId: string) => {
      try {
        setIsChangingActiveOrganizationCount((count) => count + 1);

        await dispatch(changeActiveOrganizationAction(organizationId));
        const newLocation = removeQueryParamsFromLocation(location, [
          "page",
          "search",
        ]);
        navigate(newLocation);

        setChangingActiveOrganizationError(undefined);
      } catch (err) {
        setChangingActiveOrganizationError(
          err instanceof Error ? err.message : String(err)
        );
      } finally {
        setIsChangingActiveOrganizationCount((count) => count - 1);
      }
    },
    [dispatch, location, navigate]
  );

  return {
    activeOrganization,
    changeActiveOrganization,
    isChangingActiveOrganization: Boolean(isChangingActiveOrganizationCount),
    changingActiveOrganizationError,
  };
};
